import React, { FC, useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { EProductPriority } from 'constants/enums';
import { ProductsTable } from 'views/Offers/components/ProductsTable';
import { PRODUCT_PRIORITY_OPTIONS } from 'views/Offers/Offer.consts';

import AutoCompleteInput from 'components/AutoCompleteInput/AutoCompleteInput';
import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';

import { IProductsSectionProps } from './sections.types';

export const ProductsSection: FC<IProductsSectionProps> = ({
  productOptions,
  currentProduct,
  onCurrentProductChange,
  onQuantityChange,
  onPriorityChange,
  onAddProduct,
  isAddDisabled,
  products,
  onEditProduct,
  onDeleteProduct,
  onReorderProducts,
  selectedProduct,
  addButtonDataTestId,
  productListDataTestId,
  priorityListDataTestId
}): React.ReactElement => {
  const handleAutoCompleteChange = useCallback(
    (newValue: { name: string; url: string } | null): void => {
      const matchedOption = newValue
        ? productOptions.find((o) => o.name === newValue.name)
        : null;
      onCurrentProductChange({
        productId: matchedOption?.value || '',
        quantity: currentProduct?.quantity || 0,
        priority: currentProduct?.priority || EProductPriority.SUB,
        publisherProductId: currentProduct?.publisherProductId || ''
      });
    },
    [productOptions, currentProduct, onCurrentProductChange]
  );

  const handleEdit = useCallback(
    (product: { _id: string }): void => {
      const found = products.find((p) => p._id === product._id);
      if (found) {
        onEditProduct(found);
      }
    },
    [products, onEditProduct]
  );

  const handleDelete = useCallback(
    (product: { _id: string }): void => {
      const found = products.find((p) => p._id === product._id);
      if (found) {
        onDeleteProduct(found);
      }
    },
    [products, onDeleteProduct]
  );

  const handleReorder = useCallback(
    (reordered: { _id: string }[]): void => {
      const mapped = reordered
        .map((item) => products.find((p) => p._id === item._id))
        .filter(Boolean) as typeof products;
      onReorderProducts(mapped);
    },
    [products, onReorderProducts]
  );

  const defaultPrioritySelection = useMemo(() => ({
    value: currentProduct?.priority || EProductPriority.SUB,
    label: currentProduct?.priority || EProductPriority.SUB
  }), [currentProduct?.priority]);

  return (
    <React.Fragment>
      <RichText
        title="Products"
        description="Add products to your offer"
        className="mb-5"
      />
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-3 gap-5">
          <AutoCompleteInput
            label="Product name"
            options={productOptions}
            placeholder="Search product"
            onChange={handleAutoCompleteChange}
            currentOption={selectedProduct}
          />
          <InputSelectDropdown
            label="Products Type"
            placeholder="Select Priority"
            options={PRODUCT_PRIORITY_OPTIONS}
            defaultSelected={defaultPrioritySelection}
            onOptionChange={onPriorityChange}
            listDataTestId={priorityListDataTestId}
          />
          <Input
            label="Quantity"
            name="quantity"
            value={currentProduct?.quantity ?? ''}
            type="number"
            min={0}
            onChange={onQuantityChange}
          />
        </div>
        <Button
          variant="secondary"
          disabled={isAddDisabled}
          onClick={onAddProduct}
          leftIcon={
            <FontAwesomeIcon
              icon="fa-regular fa-circle-plus"
              className="w-5 h-5"
            />
          }
          className="w-full"
          type="button"
          dataTestId={addButtonDataTestId}
        >
          Add Product
        </Button>
        <ProductsTable
          products={products}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onReorder={handleReorder}
          bodyDataTestId={productListDataTestId}
        />
      </div>
    </React.Fragment>
  );
};
